import {
  CalendarOutlined,
  EyeOutlined,
  FileTextOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Input, Modal, Select, Space, Table, Tag, Tooltip } from "antd";
import axios from "axios";
import { saveAs } from "file-saver";
import moment from "moment";
import React, { useState, useEffect, useRef } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import {
  AiOutlineCheckCircle,
  AiOutlineCloseCircle,
  AiOutlineDollarCircle,
  AiOutlineFileText,
  AiOutlineQuestionCircle,
  AiOutlineSolution,
  AiOutlineSync,
} from "react-icons/ai";
import { serverUrl } from "../../../../../nestserver";
import StatusTag from "../../../../../components/StatusTag";
import { getAcademicYearForDate } from "../../../../../utils/Const";
import { KpiStatTile } from "../dashboard360/blocks";

const { Option } = Select;
const MONTH_OPTIONS = moment.months().map((name, index) => ({
  value: index,
  label: name,
}));
const formatAgeYMD = (dob) => {
  const today = moment();
  const birth = moment(dob);
  const years = today.diff(birth, "years");
  birth.add(years, "years");
  const months = today.diff(birth, "months");
  birth.add(months, "months");
  const days = today.diff(birth, "days");
  return `${years}y ${months}m ${days}d`;
};

// Mirrors StatusTag.js's color convention so a status's KPI card and its
// table tag always match.
const STATUS_META = {
  approved: {
    label: "Approved",
    accentColor: "#389e0d",
    chipClass: "bg-emerald-50 text-emerald-600",
    icon: <AiOutlineCheckCircle />,
  },
  rejected: {
    label: "Rejected",
    accentColor: "#cf1322",
    chipClass: "bg-red-50 text-red-600",
    icon: <AiOutlineCloseCircle />,
  },
  pending: {
    label: "Pending",
    accentColor: "#d46b08",
    chipClass: "bg-amber-50 text-amber-600",
    icon: <AiOutlineFileText />,
  },
  resubmit: {
    label: "Resubmit",
    accentColor: "#2f54eb",
    chipClass: "bg-blue-50 text-blue-600",
    icon: <AiOutlineSync />,
  },
};
const DEFAULT_STATUS_META = {
  accentColor: "#595959",
  chipClass: "bg-gray-100 text-gray-500",
  icon: <AiOutlineQuestionCircle />,
};
const CORE_STATUS_KEYS = ["approved", "pending", "rejected"];

const AdmissionHomePage = ({ statusScope }) => {
  const navigate = useNavigate();

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const [selectedHistoryStatus, setSelectedHistoryStatus] = useState(
    query.get("status") || null
  );
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(
    query.get("year") || null
  );
  const [selectedMonth, setSelectedMonth] = useState(
    query.get("month") !== null ? Number(query.get("month")) : null
  );
  const [viewMessageContent, setViewMessageContent] = useState(null);

  const [tableState, setTableState] = useState({
    pagination: {
      current: Number(query.get("page")) || 1,
      pageSize: 10,
    },
  });

  const handleDownload = async (e) => {
    e.preventDefault();
    const ids = visibleApplications.map((app) => app._id).join(",");
    axios({
      url: serverUrl + "/admin/application/export",
      method: "GET",
      params: { ids },
      responseType: "blob",
      headers: {
        Accept: "application/json",
        responseType: "blob",
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    }).then((response) => {
      const scopeLabel =
        statusScope === "pending" ? "pending" : selectedHistoryStatus || "history";
      const timestamp = moment().format("YYYY-MM-DD_HHmm");
      saveAs(response.data, `admission-applications-${scopeLabel}-${timestamp}.xlsx`);
    });
  };
  // fetch applications
  async function fetchApplications() {
    const res = await axios({
      method: "get",
      url: serverUrl + "/admin/application/list/",
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }
  const applications = useQuery("applications", fetchApplications);

  // Table Settings
  const handleTableChange = (pagination, filters, sorter) => {
    setTableState({
      pagination,
      filters,
      sorter,
    });
  };

  // Keep page + History filters in the URL so they are restored on return
  // from an application's detail screen. Other params (e.g. ?tab=) are kept.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const put = (k, v) =>
      v === null || v === undefined || v === "" || v === 1
        ? params.delete(k)
        : params.set(k, v);
    put("page", tableState.pagination.current);
    put("status", selectedHistoryStatus);
    put("year", selectedAcademicYear);
    put("month", selectedMonth);
    const next = params.toString();
    if (next !== location.search.replace(/^\?/, "")) {
      navigate({ pathname: location.pathname, search: next }, { replace: true });
    }
  }, [
    tableState,
    selectedHistoryStatus,
    selectedAcademicYear,
    selectedMonth,
    navigate,
    location.pathname,
    location.search,
  ]);

  // Search Filter

  const [, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");

  const searchInputRef = useRef(null);

  const getColumnSearchProps = (dataIndex, label) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInputRef}
          placeholder={`Search ${label || dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={
              <SearchOutlined
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              />
            }
            size="small"
            style={{ width: 90, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInputRef.current?.select(), 100);
      }
    },
    render: (text) => (searchedColumn === dataIndex ? text : text),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleViewAction = (record) => {
    if (
      record.status === "approved" &&
      (record.is_registered === true || record.student?.is_registered === true)
    ) {
      navigate(`/dashboard/admin/menu/admission/view/${record?._id}`);
    } else {
      navigate(`/dashboard/admin/menu/admission/${record?._id}`);
    }
  };

  const columns = [
    {
      title: "Application No.",

      key: "",
      align: "center",
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center"
        >
          {record?.application_number}
        </div>
      ),
    },
    {
      title: "Name",
      key: "name",
      align: "center",
      ...getColumnSearchProps(["first_name", "last_name"], "Name"),
      render: (record) => (
        <div
          key={record?._id}
          style={{ color: "#111111" }}
          className=" text-base font-semibold text-center capitalize"
        >
          {record?.first_name + " " + record?.last_name}
        </div>
      ),
      onFilter: (value, record) =>
        (
          record?.first_name?.toLowerCase() +
          " " +
          record?.last_name?.toLowerCase()
        ).includes(value.toLowerCase()),
    },
    {
      title: "DOB",
      dataIndex: "dob",
      key: "dob",
      align: "center",
      render: (text) => (
        <div style={{ color: "#616161" }} className="text-base">
          {text ? moment(text).format("DD-MM-YYYY") : "—"}
        </div>
      ),
    },
    // Age helps the admin judge fit for the intended class at a glance —
    // only useful while a decision is still pending, so it's left off
    // History to avoid cluttering already-decided rows.
    ...(statusScope === "pending"
      ? [
          {
            title: "Age",
            dataIndex: "dob",
            key: "age",
            align: "center",
            sorter: (a, b) => moment(a?.dob).diff(moment(b?.dob)),
            render: (text) => (
              <div style={{ color: "#616161" }} className="text-base">
                {text ? formatAgeYMD(text) : "—"}
              </div>
            ),
          },
        ]
      : []),
    ...(statusScope === "pending"
      ? [
          {
            title: "Source",
            dataIndex: "created_by_role",
            key: "source",
            align: "center",
            filters: [
              { text: "Parent", value: "parent" },
              { text: "Admin", value: "admin" },
            ],
            onFilter: (value, record) =>
              value === "parent"
                ? record.created_by_role === "parent"
                : record.created_by_role !== "parent",
            render: (createdByRole) => (
              <Tag color={createdByRole === "parent" ? "blue" : "default"}>
                {createdByRole === "parent" ? "Parent" : "Admin"}
              </Tag>
            ),
          },
        ]
      : [
          {
            title: "Type",
            dataIndex: "is_existing_student",
            key: "application_type",
            align: "center",
            filters: [
              { text: "New", value: false },
              { text: "Existing", value: true },
            ],
            onFilter: (value, record) =>
              (record.is_existing_student === true) === value,
            render: (isExisting) => (
              <Tag color={isExisting ? "purple" : "default"}>
                {isExisting ? "Existing" : "New"}
              </Tag>
            ),
          },
        ]),
    {
      title: "Fee",
      dataIndex: "application_fee_status",
      key: "fee",
      align: "center",
      width: 100,
      filters: [
        { text: "Paid", value: "paid" },
        { text: "Pending", value: "pending" },
        { text: "Waived", value: "waived" },
        { text: "N/A", value: "not_required" },
      ],
      onFilter: (value, record) =>
        (record.application_fee_status || "not_required") === value,
      render: (feeStatus) => {
        const status = feeStatus || "not_required";
        if (status === "not_required") {
          return <span style={{ color: "#94a3b8" }}>—</span>;
        }
        const color =
          status === "paid" ? "green" : status === "waived" ? "blue" : "orange";
        const label =
          status === "paid" ? "Paid" : status === "waived" ? "Waived" : "Pending";
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Class",
      dataIndex: "classData",
      key: "class",
      align: "center",
      ...getColumnSearchProps("classData", "Class"),
      render: (classData) => (
        <div
          key={classData?._id}
          style={{ color: "#111111" }}
          className="text-base font-semibold text-center"
        >
          {classData?.name}
        </div>
      ),
      onFilter: (value, record) =>
        record.classData?.name.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: "Status",
      dataIndex: ["status"],
      key: "",
      align: "center",
      sorter: (a, b) => a.status.localeCompare(b.status),
      render: (status) => <StatusTag status={status} />,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => (
        <Space size="middle">
          <Tooltip
            title={
              record.status === "approved" ? "View Admission" : "View Application"
            }
          >
            <EyeOutlined
              className="text-blue-500 cursor-pointer"
              style={{ fontSize: "1.4rem" }}
              onClick={() => handleViewAction(record)}
            />
          </Tooltip>
          <Tooltip title={record?.message ? "View Message" : "No message"}>
            <FileTextOutlined
              className={
                record?.message
                  ? "text-blue-500 cursor-pointer"
                  : "text-gray-300 cursor-not-allowed"
              }
              style={{ fontSize: "1.4rem" }}
              onClick={() => record?.message && setViewMessageContent(record.message)}
            />
          </Tooltip>
          <Tooltip
            title={
              statusScope === "pending"
                ? `Applied: ${
                    record?.createdAt
                      ? moment(record.createdAt).format("DD-MM-YYYY")
                      : "—"
                  }`
                : `Accepted: ${
                    record?.accepted_at
                      ? moment(record.accepted_at).format("DD-MM-YYYY")
                      : "Not yet accepted"
                  }`
            }
          >
            <CalendarOutlined
              className="text-gray-400"
              style={{ fontSize: "1.4rem" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const allApplications = applications.data?.data || [];
  const statusCounts = allApplications.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {});
  const statusKeys = Object.keys(statusCounts);
  const renderedStatusKeys = Array.from(
    new Set([...CORE_STATUS_KEYS, ...statusKeys])
  );
  const feeCollected = allApplications
    .filter((app) => app.application_fee_status === "paid")
    .reduce((sum, app) => sum + (app.application_fee_amount || 0), 0);

  const groupingDate = (app) => app.accepted_at || app.createdAt;
  const academicYearOptions = Array.from(
    new Set(
      allApplications
        .map((app) => getAcademicYearForDate(groupingDate(app)))
        .filter(Boolean)
    )
  ).sort((a, b) => (a < b ? 1 : -1));

  const visibleApplications = allApplications.filter((app) => {
    if (statusScope === "pending" && app.status !== "pending") return false;
    if (statusScope === "history" && app.status === "pending") return false;
    if (
      statusScope === "history" &&
      selectedHistoryStatus &&
      app.status !== selectedHistoryStatus
    )
      return false;
    if (
      selectedAcademicYear &&
      getAcademicYearForDate(groupingDate(app)) !== selectedAcademicYear
    )
      return false;
    if (
      selectedMonth !== null &&
      moment(groupingDate(app)).month() !== selectedMonth
    )
      return false;
    return true;
  });

  return (
    <>
      <div className="mt-8">
        <div
          style={{ borderRadius: "10px", color: "#616161" }}
          className=" bg-white drop-shadow-md p-8 w-full "
        >
          <div className="flex justify-between items-center">
            <div className="text-2xl text-orange-500 font-semibold">
              Admission — {statusScope === "pending" ? "Pending" : "History"}
              {statusScope === "history" && selectedHistoryStatus && (
                <span className="text-base text-gray-500 font-normal">
                  {" "}
                  ·{" "}
                  {STATUS_META[selectedHistoryStatus]?.label ||
                    selectedHistoryStatus}
                </span>
              )}
            </div>
            <div
              className="flex items-center justify-end"
              style={{ gap: "0.75rem" }}
            >
              <Select
                allowClear
                placeholder="Academic Year"
                style={{ width: 160 }}
                value={selectedAcademicYear || undefined}
                onChange={(value) => setSelectedAcademicYear(value ?? null)}
              >
                {academicYearOptions.map((year) => (
                  <Option key={year} value={year}>
                    {year}
                  </Option>
                ))}
              </Select>
              <Select
                allowClear
                placeholder="Month"
                style={{ width: 140 }}
                value={selectedMonth ?? undefined}
                onChange={(value) => setSelectedMonth(value ?? null)}
              >
                {MONTH_OPTIONS.map((m) => (
                  <Option key={m.value} value={m.value}>
                    {m.label}
                  </Option>
                ))}
              </Select>
              <Button
                type="primary"
                htmlType="download"
                className="text-white w-9"
                onClick={(e) => handleDownload(e)}
              >
                Export
              </Button>
            </div>
          </div>
          {applications.isSuccess && (
            <div
              className="k360-grid k360-cols-2-5"
              style={{ gap: "1.5rem", marginTop: "1.5rem", marginBottom: "2rem" }}
            >
              {statusScope !== "pending" && (
                <KpiStatTile
                  compact
                  title="Total"
                  value={allApplications.length}
                  caption="All applications"
                  icon={<AiOutlineSolution />}
                  chipClass="bg-slate-100 text-slate-500"
                  accentColor="#334155"
                />
              )}
              {statusScope !== "pending" && (
                <KpiStatTile
                  compact
                  title="Fee Collected"
                  value={`₹${feeCollected}`}
                  caption="All applications"
                  icon={<AiOutlineDollarCircle />}
                  chipClass="bg-green-50 text-green-600"
                  accentColor="#16a34a"
                />
              )}
              {renderedStatusKeys.map((statusKey) => {
                const meta = STATUS_META[statusKey] || DEFAULT_STATUS_META;
                const label =
                  meta.label ||
                  statusKey.charAt(0).toUpperCase() + statusKey.slice(1);
                const count = statusCounts[statusKey] || 0;
                const isInScope =
                  statusScope === "pending"
                    ? statusKey === "pending"
                    : statusKey !== "pending";
                if (statusScope === "pending" && !isInScope) return null;
                const isClickable =
                  statusScope === "history" && isInScope && count > 0;
                const isActiveSubFilter =
                  isClickable && selectedHistoryStatus === statusKey;

                let caption;
                if (!isInScope) {
                  caption = "See Pending tab";
                } else if (count === 0) {
                  caption = `No ${label.toLowerCase()} yet`;
                } else if (isClickable) {
                  caption = isActiveSubFilter
                    ? "Showing this status"
                    : "Click to filter";
                } else {
                  caption = "Currently viewing";
                }

                return (
                  <KpiStatTile
                    compact
                    key={statusKey}
                    title={label}
                    value={count}
                    caption={caption}
                    icon={meta.icon}
                    chipClass={meta.chipClass}
                    accentColor={meta.accentColor}
                    onClick={
                      isClickable
                        ? () =>
                            setSelectedHistoryStatus(
                              isActiveSubFilter ? null : statusKey
                            )
                        : undefined
                    }
                  />
                );
              })}
            </div>
          )}
          <div className="py-5 admission-table-wrap">
            <Table
              className="admission-table"
              rowKey="_id"
              columns={columns}
              dataSource={
                applications.isSuccess
                  ? visibleApplications?.sort((a, b) =>
                      moment(a?.createdAt).isBefore(b?.createdAt) ? 1 : -1
                    )
                  : []
              }
              pagination={tableState.pagination}
              onChange={handleTableChange}
              loading={applications.isFetching}
            />
          </div>
        </div>
      </div>

      <Modal
        title="Message"
        width={800}
        visible={!!viewMessageContent}
        onCancel={() => setViewMessageContent(null)}
        footer={null}
      >
        <div className="pt-4 text-base">{viewMessageContent}</div>
      </Modal>
    </>
  );
};

export default AdmissionHomePage;
